import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { SelectBoxOption } from '../../interfaces';
import { Button, Chip, Paper, Stack, SxProps, Tooltip, Typography } from '@mui/material';

type AdditionProps = {
    title: string;
    onAddBtnClick?: () => void,
}

export type HighlightSelectProps = {
    id?: string,
    label: string,
    onChange: any,
    value: SelectBoxOption | SelectBoxOption[] | null,
    disabled?: boolean,
    size?: "small" | "medium",
    required?: boolean,
    error?: boolean,
    helperText?: React.ReactNode,
    loading?: boolean,
    margin?: "dense" | "normal" | "none",
    variant?: "standard" | "filled" | "outlined" | undefined,
    fullWdith?: boolean,
    multiple?: boolean,
    disableClearable?: boolean,
    viewOnly?: boolean,
    readOnly?: boolean
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    includeDisableStyle?: boolean,
    newOptionCreationProps?: AdditionProps,
    titleIcon?: React.ReactNode,
    sx?: SxProps;
    placeholder?: string;
}

export const HighlightSelectBox: React.FC<HighlightSelectProps & { options: SelectBoxOption[] }> = ({
    id = 'highlight-selectbox', options = [], label = "", onChange = () => null, titleIcon,
    value = { value: "", label: "" }, size = "small", margin = "normal", variant = "outlined", viewOnly = false, readOnly, onKeyDown = () => { }, includeDisableStyle = false, newOptionCreationProps, ...props }) => {
    const enableGroupBy = options[0]?.prefix ? { groupBy: (option: SelectBoxOption) => option.prefix || '' } : {};

    let selectedValue = value && !Array.isArray(value) ? value?.label || "N/A" : "N/A";

    const [isFocused, setIsFocused] = React.useState(false);

    return viewOnly ? (
        <Stack sx={{ overflowWrap: "break-word" }}>
            <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>{label}</Typography>
            {Array.isArray(value) && value.length > 0 ? <Stack
                direction={"row"}
                sx={{
                    gap: 1,
                    flexWrap: "wrap"
                }}>
                {
                    value.map(v =>
                        <Tooltip title={v?.label || "N/A"}>
                            <Chip size={"small"} label={v?.label || "N/A"} />
                        </Tooltip>
                    )
                }
            </Stack>
                : <Typography variant="body2" sx={{ fontFamily: "htrts_regular" }}>{selectedValue}</Typography>}
        </Stack>
    ) : (
        <Stack
            sx={{
                width: "100%",
                position: 'relative',
                ...(props.sx || {})
            }}>
            <Autocomplete
                // open
                id={id}
                disableClearable={props?.disableClearable || false}
                fullWidth={props.fullWdith}
                disabled={props?.disabled}
                loading={props?.loading}
                options={options}
                {...enableGroupBy}
                getOptionLabel={(option) => option?.label ?? ""}
                getOptionDisabled={(option) => !!option.disabled}
                multiple={props?.multiple}
                placeholder={props?.placeholder || ""}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            variant={variant}
                            required={props.required}
                            error={props.error}
                            helperText={props.helperText}
                            placeholder={props?.placeholder || ""}
                            label={titleIcon
                                ? <Stack
                                direction={"row"}
                                sx={{
                                    gap: 1,
                                    alignItems: "center"
                                }}>
                                    {label}
                                    {(titleIcon && (isFocused || value)) && titleIcon}
                                </Stack>
                                : label
                            }
                            margin={margin}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                        />
                    )
                }}
                size={size}
                readOnly={readOnly}
                value={value}
                onChange={(e: any, newValue: any) => onChange(newValue)}
                renderOption={(props, option, { inputValue }) => {
                    const matches = match(option.label, inputValue);
                    const parts = parse(option.label, matches);

                    return (
                        <li {...props}>
                            <div>
                                {parts.map((part: any, index: any) => (
                                    <span
                                        id={`autocomplete-${index}`}
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight ? 700 : 400,
                                        }}
                                    >
                                        {part.text}
                                    </span>
                                ))}
                            </div>
                        </li>
                    );
                }}
                disableCloseOnSelect={props?.multiple}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                // onKeyDown={onKeyDown}
                sx={includeDisableStyle ? {
                    '.MuiInputBase-root.Mui-disabled': {
                        backgroundColor: "hsl(0, 0%, 95%)"
                    }
                } : {}}
                slots={{
                    paper: ({ children }) => {
                        return (
                            <Paper>
                                {children}
                                {newOptionCreationProps && !props.disabled &&
                                    <Button
                                        color="primary"
                                        fullWidth
                                        sx={{ justifyContent: "flex-start", pl: 2 }}
                                        onMouseDown={newOptionCreationProps.onAddBtnClick}
                                    >
                                        + {newOptionCreationProps.title}
                                    </Button>
                                }
                            </Paper>
                        );
                    }
                }}
            />
        </Stack>
    );
}
